import { useState, useRef } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Button, Text, theme, Icon } from '@socrait/react';
import FlashMessage from 'react-native-flash-message';

import { ErrorBox } from '~/components/ErrorBox';
import { FlashBanner } from '~/components/FlashBanner';
import { Loading } from '~/components/Loading';
import { useViewWeeklyInsightsFeature } from '~/featureFlags';
import { useActionItemUpdateStatusMutation } from '~/services/review';
import { useRemindersListAllQuery } from '~/services/review';
import { ActionItem } from '~/services/types';
import { showError } from '~/utils/showError';

import { PeriodRemindersItem } from './PeriodRemindersItem';

export const ViewAllRemindersScreen = ({ onClose }: { onClose: () => void }) => {
  const [loadingIDs, setLoadingIDs] = useState<string[]>([]);
  const { mutate: updateActionItemStatus } = useActionItemUpdateStatusMutation();
  const notificationRef = useRef<FlashMessage>(null);

  const isViewWeeklyInsightsEnabled = useViewWeeklyInsightsFeature();

  const { data, status, fetchNextPage, isFetchingNextPage, hasNextPage } = useRemindersListAllQuery();

  const reminders = data?.pages.flatMap((page) => page?.reminders || []) || [];

  const loadReminders = () => {
    fetchNextPage();
  };

  const onUpdateActionItemStatus = (reminder: ActionItem) => {
    if (loadingIDs.includes(reminder.id)) {
      return;
    }
    setLoadingIDs((prev) => [...prev, reminder.id]);
    updateActionItemStatus(
      {
        actionItemId: reminder.id,
        status: reminder.status === 'new' ? 'completed' : 'new',
      },
      {
        onSuccess: () => {
          setLoadingIDs(loadingIDs.filter((id) => id !== reminder.id));
        },
        onError: (error: Error) => {
          setLoadingIDs(loadingIDs.filter((id) => id !== reminder.id));

          showError(error, 'Failed to update reminder status. Please try again.', notificationRef);
        },
      },
    );
  };

  return (
    <>
      <View style={styles.container}>
        <Pressable onPress={onClose} style={styles.backgroundClose} />
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Text style={styles.headerText}>All Reminders</Text>
            <Pressable onPress={onClose} style={styles.closeButton}>
              <Icon type="close" />
            </Pressable>
          </View>
          <View style={styles.remindersContainer}>
            {status === 'pending' ? (
              <Loading backgroundColor={theme.colors.white} />
            ) : status === 'error' ? (
              <ErrorBox text="Could not load reminders. Please try refreshing the page." />
            ) : (
              <View style={styles.remindersContainer}>
                {reminders.length === 0 ? (
                  <Text style={styles.remindersText}>No reminders found</Text>
                ) : (
                  <>
                    {reminders.map((reminder, index) => {
                      const displayedReminders = isViewWeeklyInsightsEnabled
                        ? reminder.reminders
                        : reminder.reminders.filter((reminder) => !reminder.insightId);

                      return (
                        <PeriodRemindersItem
                          key={`${reminder.period.id}-${index}`}
                          period={reminder.period}
                          date={reminder.date}
                          reminders={displayedReminders}
                          loadingIDs={loadingIDs}
                          onUpdateActionItemStatus={onUpdateActionItemStatus}
                        />
                      );
                    })}
                    {hasNextPage && (
                      <Button
                        onPress={loadReminders}
                        name="Load More"
                        variant="primary"
                        containerStyle={styles.loadMoreButton}
                        loading={isFetchingNextPage}
                      />
                    )}
                  </>
                )}
              </View>
            )}
          </View>
        </View>
      </View>
      <FlashMessage
        ref={notificationRef}
        position="top"
        animationDuration={100}
        duration={10000}
        MessageComponent={({ message }: { message: { message: string; type: 'warning' | 'success' } }) => (
          <FlashBanner message={message} />
        )}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  backgroundClose: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.large,
    maxHeight: 700,
    maxWidth: 1000,
    padding: theme.spacing.xlarge,
    width: '100%',
    height: '100%',
    gap: theme.spacing.xlarge,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerText: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
  },
  closeButton: {
    borderWidth: 0,
    paddingHorizontal: 0,
    height: 'initial',
  },
  remindersContainer: {
    flex: 1,
    gap: theme.spacing.medium,
    maxHeight: '100%',
    padding: 0,
    overflow: 'auto',
  },
  remindersText: {
    backgroundColor: theme.colors.gray,
    borderRadius: theme.borderRadius.large,
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    fontSize: theme.fontSizes.small,
    color: theme.colors.darkGray,
  },
  loadMoreButton: {
    alignSelf: 'center',
    marginTop: theme.spacing.xlarge,
  },
});
